import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2, Pencil } from "lucide-react";
import { api, errMsg, media, BASE, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const EMPTY = { title: "", kind: "interview", excerpt: "", content: "", cover: "", artist_id: "", published: false };
const sel = "h-10 rounded-md border border-white/10 bg-[#07090E] px-3 text-sm";

const streamDraft = async (body, onChunk) => {
  const res = await fetch(`${BASE}/api/admin/ai-draft`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("fo_token")}` }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("AI draft failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    onChunk(dec.decode(value, { stream: true }));
  }
};

export const ArticlesAdmin = () => {
  const [list, setList] = useState([]);
  const [artists, setArtists] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [notes, setNotes] = useState("");
  const [drafting, setDrafting] = useState(false);
  const load = () => api.get("/admin/articles").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/admin/artists", { params: { status: "approved" } }).then((r) => setArtists(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, artist_id: f.artist_id || null, cover: f.cover || null };
    try {
      editId ? await api.put(`/admin/articles/${editId}`, body) : await api.post("/admin/articles", body);
      toast.success("Saved"); setF(EMPTY); setEditId(null); load();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const uploadCover = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd); setF((x) => ({ ...x, cover: data.url })); } catch (er) { toast.error(errMsg(er)); }
  };
  const draft = async () => {
    if (!f.artist_id) return toast.error("Pick an artist first");
    setDrafting(true);
    setF((x) => ({ ...x, content: "" }));
    try { await streamDraft({ kind: f.kind === "review" ? "review" : "interview", artist_id: f.artist_id, notes }, (c) => setF((x) => ({ ...x, content: x.content + c }))); }
    catch (er) { toast.error(er.message); } finally { setDrafting(false); }
  };
  const edit = (a) => { setEditId(a.id); setF({ ...EMPTY, ...a, cover: a.cover || "", artist_id: a.artist_id || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { await api.delete(`/admin/articles/${id}`); load(); };

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid="admin-articles">
      <form onSubmit={save} className="panel space-y-3 p-6 lg:col-span-7" data-testid="article-form">
        <div className="label-mono">{editId ? "Edit article" : "New article"}</div>
        <Input required placeholder="Title" value={f.title} onChange={set("title")} className="border-white/10 bg-[#07090E]" data-testid="article-title-input" />
        <div className="flex flex-wrap gap-3">
          <select value={f.kind} onChange={set("kind")} className={sel} data-testid="article-kind-select"><option value="interview">Interview</option><option value="review">Review</option><option value="news">News</option></select>
          <select value={f.artist_id} onChange={set("artist_id")} className={`${sel} flex-1`} data-testid="article-artist-select"><option value="">No artist</option>{artists.map((a) => <option key={a.id} value={a.id}>{a.stage_name}</option>)}</select>
        </div>
        <Input placeholder="Excerpt" value={f.excerpt} onChange={set("excerpt")} className="border-white/10 bg-[#07090E]" data-testid="article-excerpt-input" />
        <div className="flex items-center gap-3">
          <label className="btn-ghost cursor-pointer !px-4 !py-2 text-xs">Upload cover<input hidden type="file" accept="image/*" onChange={(e) => uploadCover(e.target.files[0])} data-testid="article-cover-input" /></label>
          {f.cover && <img src={media(f.cover)} alt="" className="h-10 w-16 rounded object-cover" />}
        </div>
        <div className="rounded-xl border border-[#7B2CBF]/40 bg-[#7B2CBF]/10 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input placeholder="Notes for the AI (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="flex-1 border-white/10 bg-[#07090E]" data-testid="ai-notes-input" />
            <button type="button" onClick={draft} disabled={drafting} className="btn-ghost !px-4 !py-2 text-xs" data-testid="ai-draft-button"><Sparkles className="h-4 w-4 text-[#00F5D4]" />{drafting ? "Writing…" : "AI draft"}</button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Optional. Claude Sonnet drafts interview questions or a review from the artist profile. Always edit before publishing.</p>
        </div>
        <Textarea rows={14} placeholder="Content (blank line = new paragraph)" value={f.content} onChange={set("content")} className="border-white/10 bg-[#07090E]" data-testid="article-content-input" />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="article-published-switch" />Published</label>
          <div className="flex gap-2">{editId && <button type="button" className="btn-ghost !py-2" onClick={() => { setEditId(null); setF(EMPTY); }}>Cancel</button>}<button className="btn-hot !py-2" data-testid="article-save-button">Save</button></div>
        </div>
      </form>
      <div className="space-y-3 lg:col-span-5">
        {list.map((a) => (
          <div key={a.id} className="panel flex items-center gap-3 p-4" data-testid={`admin-article-${a.id}`}>
            <div className="min-w-0 flex-1"><div className="truncate font-display font-bold">{a.title}</div><div className="font-mono text-[10px] uppercase text-slate-500">{a.kind} · {a.published ? "published" : "draft"} · {fmtDate(a.created_at)}</div></div>
            <button onClick={() => edit(a)} className="text-slate-400 hover:text-white" data-testid={`edit-article-${a.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(a.id)} className="text-slate-500 hover:text-[#FF2E63]" data-testid={`delete-article-${a.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
